import { useState } from "react";
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Loader2,
  Check,
  TriangleAlert,
  X,
  TestTube,
  Trash2,
  Server,
  PauseCircle,
  PlayCircle,
  Activity,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/formatters";
import { getDisplayConfig, sourceTypeIcon, sourceTypeLabel } from "@/lib/integration-meta";
import {
  failedChecks,
  type RowTestState,
  type SortDir,
  type SortKey,
} from "@/lib/integration-grid";
import type { EndpointIntegrationAgent, HealthMetrics, Integration } from "@/types/api";

interface IntegrationGridProps {
  /** Already filtered + sorted by the page. */
  integrations: Integration[];
  isLoading: boolean;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  testResults: Map<string, RowTestState>;
  testingIds: Set<string>;
  onTest: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleEnabled: (id: string, enabled: boolean) => void;
  togglingIds: Set<string>;
}

const COLUMNS: { key: SortKey; label: string; className?: string }[] = [
  { key: "name", label: "Name" },
  { key: "type", label: "Type" },
  { key: "status", label: "Status" },
  { key: "last_event", label: "Last event", className: "hidden md:table-cell" },
  { key: "last_poll", label: "Last poll", className: "hidden lg:table-cell" },
  { key: "updated", label: "Updated", className: "hidden lg:table-cell" },
];

/** Freshness thresholds in milliseconds (mirrors backend defaults). */
const CLOUD_THRESHOLD_MS = 4 * 60 * 60 * 1000;

type FreshnessState = "live" | "stale" | "never";

function getFreshnessState(lastEventAt: string | null | undefined): FreshnessState {
  if (!lastEventAt) return "never";
  const age = Date.now() - new Date(lastEventAt).getTime();
  return age <= CLOUD_THRESHOLD_MS ? "live" : "stale";
}

function FreshnessChip({ lastEventAt }: { lastEventAt: string | null | undefined }) {
  const state = getFreshnessState(lastEventAt);
  if (state === "never") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <Activity className="h-3 w-3" />
        No data yet
      </span>
    );
  }
  const label = state === "live" ? "Live" : "Stale";
  const dotClass = state === "live" ? "bg-green-500" : "bg-yellow-400";
  const textClass = state === "live" ? "text-green-400" : "text-yellow-400";
  const title = lastEventAt
    ? `Last event: ${new Date(lastEventAt).toLocaleString()} (${formatRelativeTime(lastEventAt)})`
    : undefined;
  return (
    <span
      title={title}
      className={`inline-flex cursor-default items-center gap-1.5 text-xs font-medium ${textClass}`}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotClass}`} />
      {label}
    </span>
  );
}

function usedPct(
  total: number | null | undefined,
  free: number | null | undefined,
): number | null {
  if (typeof total !== "number" || typeof free !== "number") return null;
  if (!Number.isFinite(total) || !Number.isFinite(free)) return null;
  if (total <= 0 || free > total) return null;
  return Math.min(Math.max(((total - free) / total) * 100, 0), 100);
}

function cpuPct(metrics: HealthMetrics | null): number | null {
  const v = metrics?.cpu_pct;
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  return Math.min(Math.max(v, 0), 100);
}

function metricText(label: string, pct: number | null): string {
  return `${label} ${pct === null ? "—" : `${Math.round(pct)}%`}`;
}

function EndpointAgentSummary({ agents }: { agents: EndpointIntegrationAgent[] }) {
  if (agents.length === 0) return null;
  const agent = agents[0];
  const metrics = agent.health_metrics;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
        {agent.hostname || "unknown-host"}
      </code>
      <span>{agent.os_type || "unknown-os"}</span>
      <span className="capitalize">{agent.status}</span>
      <span>{metricText("Disk", usedPct(metrics?.disk_total_bytes, metrics?.disk_free_bytes))}</span>
      <span>{metricText("Mem", usedPct(metrics?.mem_total_bytes, metrics?.mem_available_bytes))}</span>
      <span>{metricText("CPU", cpuPct(metrics))}</span>
      <span>
        Heartbeat {agent.last_heartbeat_at ? formatRelativeTime(agent.last_heartbeat_at) : "never"}
      </span>
      {agents.length > 1 && <span>+{agents.length - 1} more</span>}
    </div>
  );
}

function SortHeader({
  column,
  sortKey,
  sortDir,
  onSort,
}: {
  column: { key: SortKey; label: string; className?: string };
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const active = sortKey === column.key;
  return (
    <TableHead
      className={column.className}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(column.key)}
        className="flex items-center gap-1 font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      >
        {column.label}
        {active ? (
          sortDir === "asc" ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
        )}
      </button>
    </TableHead>
  );
}

/** Compact, at-a-glance result indicator for the Actions cell. */
function testButtonFace(state: RowTestState | undefined, isTesting: boolean) {
  if (isTesting) {
    return { icon: <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />, label: "Testing..." };
  }
  if (!state) {
    return { icon: <TestTube className="mr-1.5 h-3.5 w-3.5" />, label: "Test" };
  }
  switch (state.phase) {
    case "success":
      return { icon: <Check className="mr-1.5 h-3.5 w-3.5 text-green-400" />, label: "Connected" };
    case "warning":
      return {
        icon: <TriangleAlert className="mr-1.5 h-3.5 w-3.5 text-yellow-400" />,
        label: "Warning",
      };
    case "error":
      return { icon: <X className="mr-1.5 h-3.5 w-3.5 text-red-400" />, label: "Failed" };
  }
}

function IntegrationRow({
  integration,
  selected,
  onToggleSelect,
  testState,
  isTesting,
  isToggling,
  onTest,
  onDelete,
  onToggleEnabled,
}: {
  integration: Integration;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  testState: RowTestState | undefined;
  isTesting: boolean;
  isToggling: boolean;
  onTest: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleEnabled: (id: string, enabled: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const Icon = sourceTypeIcon(integration.source_type) ?? Server;
  const displayConfig = getDisplayConfig(integration);
  const face = testButtonFace(testState, isTesting);
  const hasDetails =
    !isTesting && (testState?.phase === "warning" || testState?.phase === "error");

  return (
    <>
      <TableRow
        className={cn(selected && "bg-muted/50")}
        data-state={selected ? "selected" : undefined}
      >
        <TableCell>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(integration.id)}
            aria-label={`Select ${integration.display_name}`}
            className="h-4 w-4 rounded border-input accent-primary"
          />
        </TableCell>
        <TableCell className="font-medium">
          <div>{integration.display_name}</div>
          {integration.source_type === "endpoint" && (
            <EndpointAgentSummary agents={integration.endpoint_agents ?? []} />
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon className="h-4 w-4 shrink-0" />
            <span>{sourceTypeLabel(integration.source_type)}</span>
          </div>
        </TableCell>
        <TableCell>
          <Badge className={cn("text-[10px]", displayConfig.badge)}>
            <span className={cn("mr-1.5 inline-block h-1.5 w-1.5 rounded-full", displayConfig.dot)} />
            {displayConfig.label}
          </Badge>
        </TableCell>
        <TableCell className="hidden md:table-cell">
          <FreshnessChip lastEventAt={integration.last_event_at} />
        </TableCell>
        <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">
          {integration.last_poll_status ? (
            <span>
              {integration.last_poll_status}
              {integration.last_poll_event_count !== null && (
                <span className="ml-1">({integration.last_poll_event_count} events)</span>
              )}
            </span>
          ) : (
            "—"
          )}
        </TableCell>
        <TableCell className="hidden text-sm text-muted-foreground lg:table-cell">
          {formatRelativeTime(integration.updated_at)}
        </TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={isTesting}
              onClick={() => onTest(integration.id)}
            >
              {face.icon}
              {face.label}
            </Button>
            {hasDetails && (
              <Button
                variant="ghost"
                size="sm"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? "Hide" : "Details"}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              aria-label={
                integration.user_enabled
                  ? `Disable ${integration.display_name}`
                  : `Enable ${integration.display_name}`
              }
              disabled={isToggling}
              onClick={() => onToggleEnabled(integration.id, !integration.user_enabled)}
            >
              {isToggling ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : integration.user_enabled ? (
                <PauseCircle className="h-3.5 w-3.5" />
              ) : (
                <PlayCircle className="h-3.5 w-3.5" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Delete ${integration.display_name}`}
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(integration.id)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </TableCell>
      </TableRow>

      {expanded && hasDetails && (
        <TableRow className="bg-muted/30 hover:bg-muted/30">
          <TableCell colSpan={7} className="text-sm">
            <TestDetails testState={testState!} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function TestDetails({ testState }: { testState: RowTestState }) {
  if (testState.phase === "warning") {
    const failed = failedChecks(testState.result);
    return (
      <div className="space-y-1.5">
        <p className="font-medium text-yellow-400">
          {failed.length} check{failed.length === 1 ? "" : "s"} failed
        </p>
        <ul className="space-y-1">
          {failed.map((c, idx) => (
            <li key={`${c.name}-${idx}`} className="text-muted-foreground">
              <span className="font-medium text-foreground">{c.name}:</span> {c.detail}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (testState.phase !== "error") return null; // success → no details row
  // The page already ran describeTestError when storing the state, so error
  // here is the computed TestErrorInfo.
  const info = testState.error;
  return (
    <div className="space-y-1">
      <p className="font-medium text-red-400">{info.primary}</p>
      {info.suggestion && <p className="text-muted-foreground">{info.suggestion}</p>}
      {info.raw && (
        <p className="mt-1 rounded bg-background/60 px-2 py-1 font-mono text-xs text-muted-foreground">
          {info.raw}
        </p>
      )}
    </div>
  );
}

export function IntegrationGrid({
  integrations,
  isLoading,
  sortKey,
  sortDir,
  onSort,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  testResults,
  testingIds,
  onTest,
  onDelete,
  onToggleEnabled,
  togglingIds,
}: IntegrationGridProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (integrations.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
        No integrations match your filters
      </div>
    );
  }

  const allSelected =
    integrations.length > 0 && integrations.every((i) => selectedIds.has(i.id));
  const someSelected = integrations.some((i) => selectedIds.has(i.id)) && !allSelected;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[40px]">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={onSelectAll}
              aria-label="Select all integrations"
              className="h-4 w-4 rounded border-input accent-primary"
            />
          </TableHead>
          {COLUMNS.map((col) => (
            <SortHeader
              key={col.key}
              column={col}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
            />
          ))}
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {integrations.map((integration) => (
          <IntegrationRow
            key={integration.id}
            integration={integration}
            selected={selectedIds.has(integration.id)}
            onToggleSelect={onToggleSelect}
            testState={testResults.get(integration.id)}
            isTesting={testingIds.has(integration.id)}
            isToggling={togglingIds.has(integration.id)}
            onTest={onTest}
            onDelete={onDelete}
            onToggleEnabled={onToggleEnabled}
          />
        ))}
      </TableBody>
    </Table>
  );
}
